import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PlanCard, IntervalToggle } from "@/pages/Pricing";
import { PlatformCosts } from "@/components/PlatformCosts";
import { StripeHealth } from "@/components/StripeHealth";
import { GoLiveChecklist } from "@/components/GoLiveChecklist";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CreditCard, Loader2, ExternalLink, AlertTriangle, CheckCircle2, Users, FileText, RefreshCw } from "lucide-react";

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

const meterTone = (over, pct) => {
  if (over) return "bg-red-500";
  if (pct > 80) return "bg-amber-500";
  return "bg-emerald-500";
};

const planLabel = (st) => {
  if (st.on_trial) return `Business trial · ${st.trial_days_left} day${st.trial_days_left === 1 ? "" : "s"} left`;
  if (st.complimentary) return `${st.plan_name} plan · complimentary, no charge`;
  if (st.plan_name) return `${st.plan_name} plan · ${st.subscription_status}`;
  return "No active plan";
};

const projectionNote = (u, st) => {
  if (!u.projected_extra_gbp) return "You're within your plan allowance.";
  if (st.interval === "yearly") return "Billed automatically at the end of this monthly usage window as a separate Stripe invoice, itemised as extra users / invoice overage.";
  return "Added automatically to your next monthly Stripe invoice, itemised as extra users / invoice overage.";
};

const invoiceStatusClass = (i) => {
  if (i.paid) return "bg-emerald-500/15 text-emerald-500";
  if (i.status === "open") return "bg-amber-500/15 text-amber-500";
  return "bg-muted text-muted-foreground";
};

const Meter = ({ icon: Icon, label, used, included, extraLabel, testId }) => {
  const pct = Math.min(100, Math.round((used / Math.max(1, included)) * 100));
  const over = used > included;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /> {label}</span><span className="font-mono">{used.toLocaleString()} / {included.toLocaleString()}</span></div>
      <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden"><div className={cn("h-full rounded-full transition-all", meterTone(over, pct))} style={{ width: `${pct}%` }} /></div>
      <div className={cn("mt-1.5 text-xs", over ? "text-red-500 font-medium" : "text-muted-foreground")}>{extraLabel}</div>
    </div>
  );
};

export default function BillingPage() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [checking, setChecking] = useState(!!params.get("session_id"));

  const [invoices, setInvoices] = useState(null);
  const [interval, setInterval] = useState(params.get("interval") === "yearly" ? "yearly" : "monthly");
  const [tab, setTab] = useState(["platform", "golive"].includes(params.get("tab")) ? params.get("tab") : "plan");
  const load = useCallback(() => api.get("/billing/status").then((r) => { setSt(r.data); if (r.data.has_customer) api.get("/billing/invoices").then((x) => setInvoices(x.data.items)).catch(() => setInvoices([])); else setInvoices([]); }), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/session/${sid}`);
        if (r.data.payment_status === "paid") { toast.success("Subscription active — welcome aboard!"); await api.post("/billing/sync"); await load(); await refresh(); setChecking(false); setParams({}); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { toast.error("Payment did not complete"); setChecking(false); setParams({}); return; }
      } catch (e) { console.warn("Stripe session not confirmed yet, retrying", e?.response?.status); }
      if (++tries < 8) setTimeout(poll, 2000); else { setChecking(false); toast.message("Still confirming with Stripe — refresh in a moment."); }
    };
    poll();
  }, [params, setParams, refresh, load]);

  useEffect(() => { if (params.get("cancelled")) { toast.message("Checkout cancelled"); setParams({}); } }, [params, setParams]);

  const choose = async (p) => {
    setBusy(p.key);
    try { const r = await api.post("/billing/checkout", { plan: p.key, interval, origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setBusy(null); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const r = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = r.data.portal_url; }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setBusy(null); }
  };

  if (!st) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const u = st.usage;
  const tl = st.tax_label ? ` ${st.tax_label}` : "";
  const isAdmin = user?.role === "admin";
  const preselect = params.get("plan");

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Billing</h1>
          <p className="text-muted-foreground mt-1">{st.org_name} · <span data-testid="billing-plan-label">{planLabel(st)}</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => api.post("/billing/sync").then((r) => { setSt(r.data); toast.success("Synced with Stripe"); })} data-testid="billing-sync"><RefreshCw className="h-4 w-4 mr-1.5" /> Sync</Button>
          {st.has_customer && isAdmin && <Button data-testid="billing-portal" onClick={portal} disabled={busy === "portal"}><CreditCard className="h-4 w-4 mr-1.5" /> Manage card & invoices <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></Button>}
        </div>
      </div>

      {user?.is_platform_admin && (
        <div className="flex gap-1 rounded-lg border border-border bg-muted/30 p-1 w-fit" data-testid="billing-tabs">
          {[["plan", "Your plan"], ["platform", "Platform costs"], ["golive", "Go-live"]].map(([k, l]) => (
            <button key={k} data-testid={`billing-tab-${k}`} onClick={() => setTab(k)}
                    className={cn("rounded-md px-3 py-1.5 text-sm font-medium transition-colors", tab === k ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground")}>{l}</button>
          ))}
        </div>
      )}

      {tab === "golive" && user?.is_platform_admin && <><StripeHealth /><GoLiveChecklist /></>}
      {tab === "platform" && user?.is_platform_admin && <PlatformCosts />}
      {(tab === "plan" || !user?.is_platform_admin) && <>
      {checking && <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm flex items-center gap-2" data-testid="billing-confirming"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment with Stripe…</div>}
      {st.read_only && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm flex items-start gap-2" data-testid="billing-readonly"><AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" /> <span><strong>Your trial has ended.</strong> The workspace is read-only — choose a plan below to resume ingesting and approving invoices.</span></div>}
      {st.cancel_at_period_end && <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">Your subscription will end on {new Date(st.current_period_end).toLocaleDateString()}. Reactivate any time from the billing portal.</div>}

      <section className="grid gap-4 md:grid-cols-3" data-testid="usage-meter">
        <Meter icon={FileText} label="Invoices this period" used={u.invoices_used} included={u.invoices_included} testId="meter-invoices"
               extraLabel={u.overage_invoices ? `${u.overage_invoices} over allowance × ${gbp(u.overage_rate_gbp)} = ${gbp(u.overage_charge_gbp)}${tl} on your next bill` : `Overage above ${u.invoices_included.toLocaleString()} is ${gbp(u.overage_rate_gbp)} per invoice${tl}`} />
        <Meter icon={Users} label="Active users" used={u.users_active} included={u.seats_included} testId="meter-users"
               extraLabel={u.extra_users ? `${u.extra_users} extra user${u.extra_users > 1 ? "s" : ""} × ${gbp(u.seat_price_gbp)} = ${gbp(u.extra_users_charge_gbp)}${tl} on your next bill` : `Additional users are ${gbp(u.seat_price_gbp)} each per month${tl}`} />
        <div className={cn("rounded-xl border p-4", u.projected_extra_gbp ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card")} data-testid="projected-extra">
          <div className="text-sm text-muted-foreground">Projected extra charges this period{st.tax_label && <span className="text-[11px]"> ({st.tax_label})</span>}</div>
          <div className="mt-1 font-mono text-2xl font-bold" data-testid="projected-extra-amount">{gbp(u.projected_extra_gbp)}</div>
          <div className="text-xs text-muted-foreground mt-1">{projectionNote(u, st)}{u.charged_this_period ? " · Already billed for this period." : ""}</div>
          <div className="text-[11px] text-muted-foreground mt-2">Period {new Date(u.period_start).toLocaleDateString()} – {new Date(u.period_end).toLocaleDateString()}</div>
        </div>
      </section>

      {st.has_customer && (
        <section className="rounded-xl border border-border bg-card p-5" data-testid="billing-invoices">
          <h2 className="text-base md:text-lg font-semibold mb-1">Invoices & receipts</h2>
          <p className="text-xs text-muted-foreground mb-3">Pulled live from Stripe for your records.{st.vat_inclusive ? " All amounts include VAT." : ""} Extra users and invoice overage appear as separate lines.</p>
          {invoices === null && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {invoices?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="billing-invoices-empty">No invoices yet — your first one is issued when the trial ends.</p>}
          {invoices?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Number</th><th className="py-2 pr-3">Period</th><th className="py-2 pr-3">Items</th><th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Status</th><th className="py-2">Links</th></tr></thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-border/60" data-testid={`stripe-invoice-${i.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(i.created).toLocaleDateString()}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{i.number || "—"}</td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">{i.period_start ? `${new Date(i.period_start).toLocaleDateString()} – ${new Date(i.period_end).toLocaleDateString()}` : "—"}</td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[260px] truncate" title={i.lines.join(" · ")}>{i.lines.join(" · ") || "—"}</td>
                      <td className="py-2 pr-3 text-right font-mono">{new Intl.NumberFormat("en-GB", { style: "currency", currency: i.currency }).format(i.total)}</td>
                      <td className="py-2 pr-3"><span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", invoiceStatusClass(i))}>{i.paid ? "paid" : i.status}</span></td>
                      <td className="py-2 text-xs whitespace-nowrap">
                        {i.invoice_pdf && <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="text-primary hover:underline mr-3" data-testid={`stripe-invoice-pdf-${i.id}`}>PDF</a>}
                        {i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`stripe-invoice-view-${i.id}`}>View / receipt</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-base md:text-lg font-semibold">{st.plan && !st.on_trial ? "Change plan" : "Choose your plan"}{st.interval === "yearly" && !st.on_trial ? <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-500" data-testid="billing-interval-badge">Annual billing</span> : null}</h2>
          <IntervalToggle interval={interval} setInterval={setInterval} testId="billing-interval" />
        </div>
        {!isAdmin && <p className="text-sm text-muted-foreground mb-3">Only workspace admins can change billing.</p>}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
          {st.plans.map((p) => <PlanCard key={p.key} p={p} compact interval={interval} cta={st.on_trial ? "Subscribe" : "Choose"} current={!st.on_trial && st.plan === p.key && (st.interval || "monthly") === interval} busy={busy === p.key} onSelect={isAdmin ? choose : () => toast.error("Ask a workspace admin")} />)}
        </div>
        {preselect && <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> You picked {preselect} on the pricing page — confirm it above.</p>}
      </section>
      </>}
    </div>
  );
}
